import { makeObservable, observable, action, runInAction } from 'mobx';
import { subuserAPI, IRights } from '@api/Subuser.api';
import { RootStore } from './Root.store';

export interface ISubuser {
    subuser: string;
    devices: string[];
}

export default class SubusersStore {
    @observable
    loading: boolean;

    @observable
    addSubuserLoading: boolean;

    @observable
    serverError: boolean;

    @observable
    registerError: boolean;

    @observable
    subusersList: string[];

    @observable
    operationSuccess: boolean;

    @observable
    selectedSubuser: string | null;

    @observable
    selectedDevice: string | null;

    @observable
    subuserDevicesList: string[];

    @observable
    subusersDevices: ISubuser[];

    @observable
    selectedDeviceRights: IRights | null;

    @observable
    rightsLoading: boolean;

    @observable
    addDeviceLoading: boolean;

    @observable
    changePasswordLoading: boolean;

    @observable
    removeDeviceLoading: boolean;

    @observable
    deleteLoading: boolean;

    constructor(private rootStore: RootStore) {
        this.setDefault();
        makeObservable(this);
    }

    @action
    setDefault() {
        this.loading = false;
        this.addSubuserLoading = false;
        this.serverError = false;
        this.registerError = false;
        this.operationSuccess = false;
        this.selectedSubuser = null;
        this.selectedDevice = null;
        this.subuserDevicesList = [];
        this.subusersDevices = [];
        this.subusersList = [];
        this.selectedDeviceRights = null;
        this.rightsLoading = false;
        this.addDeviceLoading = false;
        this.changePasswordLoading = false;
        this.removeDeviceLoading = false;
        this.deleteLoading = false;
    }

    @action
    async getSubusers() {
        const token = this.rootStore.user.token;
        if (!token) return;
        this.loading = true;
        try {
            const res = await subuserAPI.getSubusers(token);
            if (res.result === 'success') {
                runInAction(() => {
                    this.subusersList = res.subusers;
                    this.subusersDevices = [];
                });
                await this.getSubusersDevices();
                runInAction(() => {
                    this.loading = false;
                });
            } else {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.loading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async create(login: string, password: string) {
        const token = this.rootStore.user.token;
        if (!token) return;
        this.addSubuserLoading = true;
        try {
            const res = await subuserAPI.create(token, login, password);
            if (res.result === 'success') {
                this.addSubuserLoading = false;
                this.operationSuccess = true;
                this.getSubusers();
            } else if (
                res.result === 'error' &&
                res.info.indexOf('already_exists') >= 0
            ) {
                runInAction(() => {
                    this.addSubuserLoading = false;
                    this.registerError = true;
                });
            } else {
                runInAction(() => {
                    this.addSubuserLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.addSubuserLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async changePassword(password: string) {
        const token = this.rootStore.user.token;
        const login = this.selectedSubuser;
        if (!token || !login) return;
        this.changePasswordLoading = true;
        try {
            const res = await subuserAPI.changePassword(token, login, password);
            if (res.result === 'success') {
                this.changePasswordLoading = false;
                this.operationSuccess = true;
                this.getSubusers();
            } else {
                runInAction(() => {
                    this.changePasswordLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.changePasswordLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async delete() {
        const token = this.rootStore.user.token;
        const login = this.selectedSubuser;
        if (!token || !login) return;
        this.deleteLoading = true;
        try {
            const res = await subuserAPI.delete(token, login);
            if (res.result === 'success') {
                this.deleteLoading = false;
                this.operationSuccess = true;
                this.getSubusers();
            } else {
                runInAction(() => {
                    this.deleteLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.deleteLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async getSubuserDevices(login: string) {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            const res = await subuserAPI.getDevices(token, login);
            if (res.result === 'success') {
                const devices = res.devices;
                const subuser = login;
                const item = { subuser, devices };
                runInAction(() => {
                    this.subusersDevices = [...this.subusersDevices, item];
                });
            } else {
                runInAction(() => {
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.serverError = true;
            });
        }
    }

    @action
    async getSubusersDevices() {
        await Promise.all(
            this.subusersList.map(async (login) => {
                await this.getSubuserDevices(login);
            }),
        );
    }

    @action
    async getDevices() {
        const token = this.rootStore.user.token;
        const login = this.selectedSubuser;
        if (!token || !login) return;
        this.loading = true;
        try {
            const res = await subuserAPI.getDevices(token, login);
            if (res.result === 'success') {
                this.loading = false;
                this.subuserDevicesList = res.devices;
            } else {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.loading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async addDevice(imei: string, rights: IRights) {
        const token = this.rootStore.user.token;
        const login = this.selectedSubuser;
        if (!token || !login) return;
        this.addDeviceLoading = true;
        try {
            const args = {
                token,
                login,
                imei,
                rights,
            };
            const res = await subuserAPI.addDevice(args);
            if (res.result === 'success') {
                this.addDeviceLoading = false;
                this.operationSuccess = true;
                this.getSubusers();
            } else {
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.addDeviceLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async removeDevice() {
        const token = this.rootStore.user.token;
        const login = this.selectedSubuser;
        const imei = this.selectedDevice;
        if (!token || !login || !imei) return;
        this.removeDeviceLoading = true;
        try {
            const args = {
                token,
                login,
                imei,
            };
            const res = await subuserAPI.removeDevice(args);
            if (res.result === 'success') {
                this.removeDeviceLoading = false;
                this.operationSuccess = true;
                this.getDevices();
            } else {
                runInAction(() => {
                    this.removeDeviceLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.removeDeviceLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async getRights() {
        const token = this.rootStore.user.token;
        const login = this.selectedSubuser;
        const imei = this.selectedDevice;
        if (!token || !login || !imei) return;
        this.rightsLoading = true;
        try {
            const args = {
                token,
                login,
                imei,
            };
            const res = await subuserAPI.getRights(args);
            if (res.result === 'success') {
                this.rightsLoading = false;
                this.selectedDeviceRights = res.rights;
            } else {
                runInAction(() => {
                    this.rightsLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.rightsLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async changeRights(rights: IRights) {
        const token = this.rootStore.user.token;
        const login = this.selectedSubuser;
        const imei = this.selectedDevice;
        if (!token || !login || !imei) return;
        this.rightsLoading = true;
        try {
            const args = {
                token,
                login,
                imei,
                rights,
            };
            const res = await subuserAPI.changeRights(args);
            if (res.result === 'success') {
                this.rightsLoading = false;
                this.operationSuccess = true;
            } else {
                runInAction(() => {
                    this.rightsLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.rightsLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    setSelectedDevice(imei: string) {
        this.selectedDevice = imei;
    }

    @action
    resetSelectedDevice() {
        this.selectedDevice = null;
    }

    @action
    resetServerError() {
        this.serverError = false;
    }

    @action
    resetRegisterError() {
        this.registerError = false;
    }

    @action
    resetOperationSuccess() {
        this.operationSuccess = false;
    }

    @action
    setSelectedSubuser(login: string) {
        this.selectedSubuser = login;
    }

    @action
    resetSelectedSubuser() {
        this.selectedSubuser = null;
    }

    @action
    resetSubuserDevicesList() {
        this.subuserDevicesList = [];
    }

    @action
    resetSelectedDeviceRights() {
        this.selectedDeviceRights = null;
    }
}
